<script setup lang="ts">
import {
  Activity,
  CircleCheck,
  Clock3,
  Coins,
  LoaderCircle,
  Search,
} from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"
import { useRoute } from "vue-router"

import AppSelect from "../../components/AppSelect.vue"
import { apiRequest } from "../../lib/api"
import { formatShanghaiTime } from "../../lib/datetime"
import type { AdminCallItem, AdminCallList, AdminCallSummary } from "../../lib/types"

const route = useRoute()
const calls = ref<AdminCallItem[]>([])
const summary = ref<AdminCallSummary | null>(null)
const total = ref(0)
const page = ref(1)
const pageSize = 50
const loading = ref(true)
const error = ref("")
const search = ref("")
const platform = ref("")
const status = ref("")
const hours = ref("24")
const providerAccountId = ref(
  typeof route.query.provider_account_id === "string"
    ? route.query.provider_account_id
    : "",
)
const timeOptions = [
  { value: "24", label: "最近 24 小时" },
  { value: "168", label: "最近 7 天" },
  { value: "720", label: "最近 30 天" },
  { value: "2160", label: "最近 90 天" },
]
const platformOptions = [
  { value: "", label: "全部平台" },
  { value: "rednote", label: "小红书" },
  { value: "zhihu", label: "知乎" },
  { value: "tophub", label: "TopHub" },
  { value: "douban", label: "豆瓣" },
  { value: "weibo", label: "微博 CLI" },
  { value: "weibo_web", label: "微博 Web" },
  { value: "smzdm", label: "什么值得买" },
  { value: "bilibili", label: "哔哩哔哩" },
  { value: "douyin", label: "抖音" },
  { value: "v2ex", label: "V2EX" },
  { value: "xueqiu", label: "雪球" },
  { value: "reddit", label: "Reddit" },
  { value: "twitter", label: "Twitter / X" },
  { value: "xianyu", label: "闲鱼" },
  { value: "jd", label: "京东" },
  { value: "1688", label: "1688" },
  { value: "linuxdo", label: "Linux.do" },
  { value: "nowcoder", label: "牛客" },
  { value: "instagram", label: "Instagram" },
  { value: "maimai", label: "脉脉" },
  { value: "jianyu", label: "剑鱼标讯" },
  { value: "toutiao", label: "今日头条" },
  { value: "sinafinance", label: "新浪财经" },
  { value: "juejin", label: "掘金" },
  { value: "36kr", label: "36氪" },
  { value: "dongchedi", label: "懂车帝" },
  { value: "ctrip", label: "携程" },
  { value: "boss", label: "BOSS 直聘" },
  { value: "linkedin", label: "LinkedIn" },
]
const statusOptions = [
  { value: "", label: "全部状态" },
  { value: "succeeded", label: "成功" },
  { value: "failed", label: "失败" },
  { value: "running", label: "执行中" },
]

const successRate = computed(() => {
  if (!summary.value?.total_calls) return "0%"
  return `${Math.round((summary.value.succeeded_calls / summary.value.total_calls) * 100)}%`
})
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize)))

onMounted(load)

async function load(resetPage = false): Promise<void> {
  if (resetPage) page.value = 1
  loading.value = true
  error.value = ""
  const params = new URLSearchParams({
    hours: hours.value,
    page: String(page.value),
    page_size: String(pageSize),
  })
  if (search.value.trim()) params.set("search", search.value.trim())
  if (platform.value) params.set("platform", platform.value)
  if (status.value) params.set("status", status.value)
  if (providerAccountId.value) params.set("provider_account_id", providerAccountId.value)
  try {
    const result = await apiRequest<AdminCallList>(`/v1/admin/calls?${params}`)
    calls.value = result.items
    summary.value = result.summary
    total.value = result.total
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载调用记录失败"
  } finally {
    loading.value = false
  }
}

async function changePage(next: number): Promise<void> {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  await load()
}

function formatDate(value: string): string {
  return formatShanghaiTime(value, {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

function formatDuration(value: number | null): string {
  if (value === null) return "—"
  return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${value}ms`
}

function statusLabel(value: AdminCallItem["status"]): string {
  return { succeeded: "成功", failed: "失败", running: "执行中" }[value]
}

function platformMark(value: AdminCallItem["platform"]): string {
  return { rednote: "RED", zhihu: "知", tophub: "TOP", douban: "豆", weibo: "微", weibo_web: "WEB", smzdm: "值", bilibili: "哔", v2ex: "V2", xueqiu: "雪", reddit: "R", twitter: "X", xianyu: "闲", jd: "京", "1688": "1688", linuxdo: "L", nowcoder: "牛", instagram: "IG", maimai: "脉", jianyu: "剑", toutiao: "头", sinafinance: "财", juejin: "掘", "36kr": "36", dongchedi: "车", ctrip: "旅", boss: "聘", linkedin: "in" }[value]
}

function usesManagedAccount(value: AdminCallItem["platform"]): boolean {
  return ["rednote", "zhihu", "douban", "weibo", "weibo_web", "bilibili", "xueqiu", "reddit", "twitter", "xianyu", "jd", "linuxdo", "nowcoder", "instagram", "maimai", "jianyu", "dongchedi", "ctrip", "boss", "linkedin"].includes(value)
}
</script>

<template>
  <section class="admin-page">
    <div class="admin-page-heading">
      <div><span class="eyebrow">API observability</span><h1>调用记录</h1><p>按用户、API Key、接口和平台账号追踪每一次 REST / MCP 调用。</p></div>
      <div class="admin-count"><strong>{{ total.toLocaleString() }}</strong><span>筛选范围内调用</span></div>
    </div>

    <section class="admin-metric-grid usage-metric-grid">
      <article><span><Activity :size="16" />调用次数</span><strong>{{ summary?.total_calls.toLocaleString() ?? "—" }}</strong><small>{{ summary?.failed_calls ?? 0 }} 次失败 · {{ summary?.running_calls ?? 0 }} 次执行中</small></article>
      <article><span><CircleCheck :size="16" />成功率</span><strong>{{ successRate }}</strong><small>{{ summary?.succeeded_calls ?? 0 }} 次成功</small></article>
      <article><span><Coins :size="16" />积分消耗</span><strong>{{ summary?.total_credits.toLocaleString() ?? "—" }}</strong><small>返回 {{ summary?.total_items.toLocaleString() ?? 0 }} 条内容</small></article>
      <article><span><Clock3 :size="16" />平均耗时</span><strong>{{ formatDuration(summary?.avg_duration_ms ?? null) }}</strong><small>仅统计实际进入上游的调用</small></article>
    </section>

    <div class="admin-toolbar usage-toolbar">
      <label class="admin-search"><Search :size="15" /><input v-model="search" placeholder="用户邮箱、API Key、接口或平台账号" @keyup.enter="load(true)" /></label>
      <AppSelect v-model="hours" class="admin-select" compact aria-label="时间范围" :options="timeOptions" @change="load(true)" />
      <AppSelect v-model="platform" class="admin-select" compact aria-label="平台筛选" :options="platformOptions" @change="load(true)" />
      <AppSelect v-model="status" class="admin-select" compact aria-label="调用状态筛选" :options="statusOptions" @change="load(true)" />
      <button class="secondary-button" @click="load(true)">查询</button>
    </div>

    <p v-if="error" class="form-error">{{ error }}</p>
    <div class="admin-table-shell">
      <div v-if="loading" class="admin-loading"><LoaderCircle class="spin" :size="20" />正在读取调用记录</div>
      <div v-else-if="calls.length === 0" class="admin-empty">当前筛选范围内没有调用记录。</div>
      <div v-else class="responsive-table">
        <table class="admin-table usage-table">
          <thead><tr><th>时间（上海）</th><th>用户 / API Key</th><th>接口</th><th>平台账号</th><th>结果</th><th>消耗 / 性能</th></tr></thead>
          <tbody>
            <tr v-for="call in calls" :key="call.request_id">
              <td><strong class="usage-time">{{ formatDate(call.received_at) }}</strong><small class="table-subline">{{ call.protocol.toUpperCase() }}</small></td>
              <td><div class="usage-identity"><strong>{{ call.user_email || call.workspace_name }}</strong><small>{{ call.api_key_name }} · ••••{{ call.api_key_last_four }}</small></div></td>
              <td><code class="usage-operation">{{ call.operation }}</code><small class="table-subline">{{ call.request_id }}</small></td>
              <td><div class="usage-account"><span class="platform-mark" :class="`platform-mark--${call.platform}`">{{ platformMark(call.platform) }}</span><span><strong>{{ usesManagedAccount(call.platform) ? call.provider_account_name || "未分配账号" : "内嵌 Provider" }}</strong><small>{{ usesManagedAccount(call.platform) ? call.provider_account_id || "调度前失败或历史调用" : "无需登录凭证" }}</small></span></div></td>
              <td><span class="status-pill" :class="{ 'status-pill--active': call.status === 'succeeded', 'status-pill--expired': call.status === 'failed' }"><i></i>{{ statusLabel(call.status) }}</span><small v-if="call.error_code" class="table-subline usage-error">{{ call.error_code }}</small><small v-else-if="call.http_status" class="table-subline">HTTP {{ call.http_status }}</small></td>
              <td><strong class="usage-cost">{{ call.credits }} credits</strong><small class="table-subline">{{ formatDuration(call.duration_ms) }} · {{ call.item_count ?? 0 }} items</small></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <footer v-if="total > pageSize" class="usage-pagination">
      <span>第 {{ page }} / {{ totalPages }} 页</span>
      <div><button class="secondary-button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button><button class="secondary-button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button></div>
    </footer>
  </section>
</template>

<style scoped>
.usage-toolbar .admin-search { flex: 1; max-width: 440px; }
.usage-table { min-width: 1120px; }
.usage-table td { height: 78px; }
.usage-time, .usage-cost { font: 600 10px monospace; white-space: nowrap; }
.usage-identity { min-width: 170px; display: grid; gap: 3px; }
.usage-identity strong, .usage-account strong { overflow: hidden; max-width: 220px; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.usage-identity small, .usage-account small { overflow: hidden; max-width: 220px; color: var(--muted); font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.usage-operation { color: var(--ink); font-size: 10px; white-space: nowrap; }
.usage-account { min-width: 190px; display: flex; align-items: center; gap: 9px; }
.usage-account .platform-mark { width: 29px; height: 29px; flex: none; }
.usage-account > span:last-child { min-width: 0; display: grid; gap: 2px; }
.usage-error { max-width: 150px; overflow: hidden; color: var(--accent) !important; text-overflow: ellipsis; white-space: nowrap; }
.usage-pagination { display: flex; align-items: center; justify-content: space-between; gap: 18px; color: var(--muted); font-size: 10px; }
.usage-pagination > div { display: flex; gap: 8px; }

@media (max-width: 760px) {
  .usage-toolbar .admin-search { max-width: none; }
  .usage-pagination { align-items: stretch; flex-direction: column; }
}
</style>
